import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { ILogRecord, ICardLogConfig, ILogContext, ILogLink } from '@shared/interface/log.interface';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { UtilitiesService } from '@services/utilities.service';
import { BaseDatasource } from '@shared/base/base.datasource';
import { BaseTableService } from '@shared/base/base-table-service.service';
import { UrlUtilsService } from '@utils/url-utils.service';
import { IQueryParams } from '@shared/interface/urlparam.interface';
import { TableContentComponent } from '../../table-content/table-content.component';
import { BottomTableComponent } from '../../bottom-table/bottom-table.component';
import { CardComponent, CardEmptyComponent } from '@shared/components/card';
import { BaseComponent } from '@shared/base/base.component';
import { switchMap, tap } from 'rxjs';

/**
 * Processed log record dengan generated fields
 * Menggunakan intersection type karena ILogRecord adalah union type
 */
type IProcessedLogRecord = ILogRecord & {
  _link?: ILogLink | null;
  _reasonText?: string | null;
};

@Component({
  selector: 'app-card-log',
  standalone: true,
  imports: [CommonModule, RouterModule, InlineSVGModule, CardComponent, CardEmptyComponent, TableContentComponent, BottomTableComponent],
  templateUrl: './card-log.component.html',
  styleUrls: ['../card-log-legacy/card-log-legacy.component.scss'],
})
export class CardLogComponent extends BaseComponent implements OnInit {
  @Input() config!: ICardLogConfig;
  @Input() context?: ILogContext;
  // Optional — kalau di-set, judul dirender DI DALAM card yang sama (cardHeader), bukan
  // ditaruh manual di luar `<app-card-log>` oleh consumer (beda halaman jadi konsisten satu pola).
  @Input() title?: string;

  baseDatasourceLog: BaseDatasource<ILogRecord>;
  processedDataLog: IProcessedLogRecord[] = [];

  STRING_CONSTANTS = STRING_CONSTANTS;
  iconNone = STRING_CONSTANTS.ICON.IC_RETAIL_NONE;

  constructor(
    private baseTableService: BaseTableService<ILogRecord>,
    private paginationService: UrlUtilsService,
    public utilities: UtilitiesService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private ref: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.initDataSource();
    this.queryHandler();
  }

  initDataSource() {
    this.baseDatasourceLog = new BaseDatasource<ILogRecord>();
    this.baseTableService.responseDatabase
      .pipe(
        this.takeUntilDestroy(),
        tap((resp) => (this.baseDatasourceLog = resp)),
        switchMap((resp) => resp.tableSubject)
      )
      .subscribe((data) => {
        this.processedDataLog = this.generateDataLog(data);
      });
  }

  queryHandler() {
    this.activatedRoute.queryParams.pipe(this.takeUntilDestroy()).subscribe((params) => {
      if (params.size) this.baseDatasourceLog.pageSize = +params.size;
      if (params.page !== undefined) this.baseDatasourceLog.current_page = +params.page;

      // Use excludeQueryParams from config if provided
      // Service will handle undefined gracefully (no exclusion if undefined)
      const param = this.paginationService.sliceQueryParams(this.config.excludeQueryParams);

      this.baseTableService.loadDataTable(this.config.urlDataApi, param ? param : '');
    });
  }

  changePageEvent($event: BaseDatasource<IQueryParams>) {
    const { pageSize, current_page } = $event;

    const dataParam: IQueryParams = {
      page: current_page,
      size: pageSize,
    };

    this.router
      .navigate([], {
        queryParams: dataParam,
        queryParamsHandling: 'merge',
      })
      .then(() => null);
  }

  generateDataLog(data: ILogRecord[]): IProcessedLogRecord[] {
    if (!data || data.length === 0) return [];

    return data.map((record) => {
      // Generate link jika ada linkGenerator
      const link = this.config.linkGenerator ? this.config.linkGenerator(record, this.context) : null;

      // Generate reason text jika ada reasonFormatter
      const reasonText = this.config.reasonFormatter ? this.config.reasonFormatter(record) : null;

      return {
        ...record,
        _link: link,
        _reasonText: reasonText,
      };
    });
  }

  navigateToDetail(link: ILogLink): void {
    if (!link || !link.url) {
      return;
    }

    this.router.navigate([link.url], { queryParams: link.queryParams, queryParamsHandling: 'merge' }).then();
  }
}
